import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, TextField, CircularProgress,
  Grid, Avatar, LinearProgress, Tooltip,
} from '@mui/material';
import { People, Print, TrendingUp } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-PK') : 'Never';
const roleColor = { owner: 'primary', manager: 'info', cashier: 'default', super_admin: 'error' };

export default function StaffReportPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [to, setTo] = useState(new Date().toISOString().split('T')[0]);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/staff', { params: { from, to } });
      setStaff(data.data || []);
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const maxRevenue = Math.max(...staff.map(s => +s.total_revenue), 1);
  const topCashier = staff.find(s => s.total_revenue > 0);
  const totalRevenue = staff.reduce((s, u) => s + +u.total_revenue, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <People color="primary" />
          <Typography variant="h5" fontWeight={700}>Staff Performance / عملہ رپورٹ</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, pb: '16px !important', flexWrap: 'wrap' }}>
          <TextField label="From" type="date" size="small" value={from}
            onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to}
            onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Generate'}
          </Button>
          {/* Quick ranges */}
          {[
            { label: 'Today', fn: () => { const t = new Date().toISOString().split('T')[0]; setFrom(t); setTo(t); } },
            { label: 'This Month', fn: () => { const d = new Date(); d.setDate(1); setFrom(d.toISOString().split('T')[0]); setTo(new Date().toISOString().split('T')[0]); } },
            { label: 'Last 30d', fn: () => { const d = new Date(); d.setDate(d.getDate() - 30); setFrom(d.toISOString().split('T')[0]); setTo(new Date().toISOString().split('T')[0]); } },
          ].map(r => (
            <Button key={r.label} size="small" variant="outlined" onClick={r.fn}>{r.label}</Button>
          ))}
        </CardContent>
      </Card>

      <Box ref={printRef}>
        <style>{`@media print { @page { size: A4; margin: 15mm; } }`}</style>

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Total Staff Active</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">{staff.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Combined Revenue</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">₨ {fmt(totalRevenue)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Top Performer</Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  {topCashier?.name || '—'}
                </Typography>
                {topCashier && <Typography variant="caption" color="text.secondary">₨ {fmt(topCashier.total_revenue)}</Typography>}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bar chart */}
        {staff.filter(s => s.total_invoices > 0).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Revenue by Staff Member</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={staff.filter(s => s.total_invoices > 0).map(s => ({ name: s.name.split(' ')[0], revenue: +s.total_revenue, invoices: +s.total_invoices }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={v => `₨${(v / 1000).toFixed(0)}k`} />
                  <RTooltip formatter={(v, n) => [n === 'revenue' ? `₨ ${fmt(v)}` : v, n === 'revenue' ? 'Revenue' : 'Invoices']} />
                  <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Staff table */}
        <Card>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Staff Member</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Discounts</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Sale</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Performance</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Sale</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data</TableCell></TableRow>
                )}
                {staff.map(u => {
                  const pct = maxRevenue > 0 ? (+u.total_revenue / maxRevenue) * 100 : 0;
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
                            {u.name?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={u.role} size="small" color={roleColor[u.role] || 'default'} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell align="right"><Typography fontWeight={700}>{u.total_invoices}</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight={700} color="primary.main">₨ {fmt(u.total_revenue)}</Typography></TableCell>
                      <TableCell align="right"><Typography color="success.main">₨ {fmt(u.total_collected)}</Typography></TableCell>
                      <TableCell align="right">
                        {u.total_discounts > 0
                          ? <Typography color="warning.main">₨ {fmt(u.total_discounts)}</Typography>
                          : <Typography color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">₨ {fmt(u.avg_sale)}</TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <Tooltip title={`${pct.toFixed(1)}% of top performer`}>
                          <Box>
                            <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} color={pct > 70 ? 'success' : pct > 30 ? 'warning' : 'error'} />
                            <Typography variant="caption" color="text.secondary">{pct.toFixed(0)}%</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{fmtDate(u.last_sale_at)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </Box>
    </Box>
  );
}
